<template>
    <VJoyTemplate :loading="isStepLoading" class="profile__resume-import__experience-duplicates">
        <template #template-title>{{ t('profile.experience.funnel.step.duplicate_list.step_title') }}</template>
        <template #template-main>
            <template v-for="(duplicates, index) in experiencesWithDuplicates" :key="`resume-import-experience-with-duplicates--${index}`">
                <div class="profile__resume-import__experience-duplicates__item">
                    <div class="profile__resume-import__experience-duplicates__item__resume">
                        <template
                            v-for="experienceFromResume in duplicates.resume"
                            :key="`resume-import-experience-with-duplicates--${index}--resume--${experienceFromResume.id}`"
                        >
                            <ResumeImportExperienceComponent
                                v-if="!experienceIdsToRemove.includes(experienceFromResume.id!)"
                                :experience="experienceFromResume"
                                :is-visible-cta-edit="false"
                                @resume-import-experience:delete="handleResumeImportExperienceDelete"
                            />
                        </template>
                    </div>
                    <div
                        class="profile__resume-import__experience-duplicates__item__separator"
                        :class="{
                            'profile__resume-import__experience-duplicates__item__separator--hidden':
                                duplicates.resume.length === 0 || duplicates.malt.length === 0,
                        }"
                    >
                        {{ t('profile.experience.funnel.step.duplicate_list.duplicates.or') }}
                    </div>
                    <div class="profile__resume-import__experience-duplicates__item__malt">
                        <div
                            v-for="experienceFromMalt in duplicates.malt"
                            :key="`resume-import-experience-with-duplicates--${index}--malt--${experienceFromMalt.id}`"
                        >
                            <ResumeImportExperienceComponent
                                v-if="!experienceIdsToRemove.includes(experienceFromMalt.id!)"
                                :experience="experienceFromMalt"
                                :is-visible-cta-edit="false"
                                @resume-import-experience:delete="handleResumeImportExperienceDelete"
                            />
                        </div>
                    </div>
                </div>
                <VJoySeparator
                    v-if="index < experiencesWithDuplicates.length - 1"
                    :text="t('profile.experience.funnel.step.duplicate_list.duplicates.separator')"
                    size="small"
                />
            </template>
        </template>
    </VJoyTemplate>
</template>
<script setup lang="ts">
    import {onMounted, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useDataTracking, useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoySeparator, VJoyTemplate} from '@maltjoy/core-vue';
    import {ResumeImportStep, TrackingEvents} from '~/config/TrackingEvents';
    import {useDefineFunnelNextStepHandler} from '~/features/resume-import/composable/useDefineFunnelNextStepHandler';
    import ResumeImportExperienceComponent from '~/features/resume-import/ui/ResumeImportExperience.vue';
    import {useResumeImportStore} from '../../store/useResumeImportStore';
    import {type ResumeImportExperience, ResumeImportExperienceSource} from '../../types/ResumeImportExperience';

    type ResumeImportExperienceDuplicates = {
        resume: ResumeImportExperience[];
        malt: ResumeImportExperience[];
    };

    const emit = defineEmits<{
        'funnel-step:skip-step': [];
    }>();

    const {t} = useTranslation();
    const {track} = useDataTracking();
    useDefineFunnelNextStepHandler(stepNextAction);

    const storeResumeImport = useResumeImportStore();
    const {isFunnelCtaNextEnabled, resumeImportDuplicatesList, resumeImportExperienceList} = storeToRefs(storeResumeImport);
    const isStepLoading = ref(true);

    const experienceIdsToRemove = ref<string[]>([]);
    const experiencesWithDuplicates = ref<ResumeImportExperienceDuplicates[]>([]);

    watch(
        [resumeImportDuplicatesList, resumeImportExperienceList, experienceIdsToRemove],
        () => {
            listExperiencesWithDuplicates();
        },
        {deep: true},
    );

    onMounted(() => {
        if (!resumeImportDuplicatesList.value || resumeImportDuplicatesList.value.length === 0) {
            emit('funnel-step:skip-step');
        } else {
            listExperiencesWithDuplicates();
            isFunnelCtaNextEnabled.value = true;
            isStepLoading.value = false;
            track(TrackingEvents.RESUME_IMPORT_FUNNEL, {funnel_step: ResumeImportStep.DUPLICATED_EXPERIENCES});
        }
    });

    async function stepNextAction(): Promise<ResumeImportExperience[]> {
        resumeImportExperienceList.value = resumeImportExperienceList.value.filter(
            (experience) => !experienceIdsToRemove.value.includes(experience.id!),
        );

        return await Promise.resolve(resumeImportExperienceList.value);
    }

    function listExperiencesWithDuplicates() {
        const tmpExperiencesWithDuplicates: ResumeImportExperienceDuplicates[] = [];

        resumeImportDuplicatesList.value.forEach((duplicatedExperienceIds) => {
            const newDuplicatesToAdd: ResumeImportExperienceDuplicates = {
                malt: [],
                resume: [],
            };

            duplicatedExperienceIds.forEach((experienceId) => {
                const experienceToAdd = resumeImportExperienceList.value.find((experience) => experience.id === experienceId);

                if (experienceToAdd && !experienceIdsToRemove.value.includes(experienceToAdd.id!)) {
                    if (experienceToAdd.source === ResumeImportExperienceSource.MALT) {
                        newDuplicatesToAdd.malt.push(experienceToAdd);
                    } else {
                        newDuplicatesToAdd.resume.push(experienceToAdd);
                    }
                }
            });

            tmpExperiencesWithDuplicates.push(newDuplicatesToAdd);
        });

        experiencesWithDuplicates.value = tmpExperiencesWithDuplicates;
    }

    function handleResumeImportExperienceDelete(deletedExperience: ResumeImportExperience) {
        if (deletedExperience.id) {
            experienceIdsToRemove.value.push(deletedExperience.id);
            pushVJoySnackbar({
                props: {
                    message: t('profile.experience.funnel.step.duplicate_list.notification.removed'),
                    level: 'info',
                },
            });
        }
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .profile__resume-import__experience-duplicates {
        &__item {
            display: flex;
            flex-direction: row;
            gap: var(--joy-core-spacing-2);

            @include mq.screen_xs {
                flex-direction: column;
            }

            &__malt,
            &__resume {
                width: 50%;
                display: flex;
                flex-direction: column;
                gap: var(--joy-core-spacing-2);

                @include mq.screen_xs {
                    width: 100%;

                    .profile__resume-import__experience {
                        margin-bottom: 0;
                    }
                }
            }

            &__malt {
                > div {
                    display: flex;
                    flex-direction: row;
                    gap: var(--joy-core-spacing-2);
                }
            }

            &__separator {
                margin-top: var(--joy-core-spacing-6);
                font-weight: var(--joy-font-weight-bold);

                @include mq.screen_xs {
                    margin: 0 auto;
                }

                &--hidden {
                    // keep the item in the DOM for alignement purpose
                    visibility: hidden;
                }
            }
        }

        .joy-separator {
            // border: none;
            // height: 1px;
            // width: 66%;
            // background-color: var(--joy-color-neutral-50);
            margin-bottom: var(--joy-core-spacing-8);
            margin-top: 0;
            @include text.getFontBySize(primary-200);

            @include mq.screen_xs {
                margin-top: var(--joy-core-spacing-8);
            }
        }
    }
</style>
